import type { ConclaveSpeakingState } from '@/lib/conclave-api';

export type SpeakingClockTone = 'ok' | 'warning' | 'overtime';

export function speakingClockTone(
  remainingSeconds: number,
  totalSeconds: number
): SpeakingClockTone {
  if (remainingSeconds <= 0) return 'overtime';
  if (totalSeconds > 0 && remainingSeconds <= totalSeconds * 0.05) return 'warning';
  return 'ok';
}

export function formatSpeakingClock(totalSeconds: number): string {
  const negative = totalSeconds < 0;
  const safe = Math.abs(Math.trunc(totalSeconds));
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const seconds = safe % 60;
  const mm = String(minutes).padStart(2, '0');
  const ss = String(seconds).padStart(2, '0');
  const body = hours > 0 ? `${hours}:${mm}:${ss}` : `${mm}:${ss}`;
  return negative ? `-${body}` : body;
}

function elapsedSeconds(startedAt: string, now: number, offsetMs: number): number {
  const started = new Date(startedAt).getTime();
  if (Number.isNaN(started)) return 0;
  return Math.max(0, Math.floor((now + offsetMs - started) / 1000));
}

export function sessionRemainingSeconds(
  state: ConclaveSpeakingState | null,
  now: number,
  offsetMs: number
): number | null {
  if (!state?.sessionTimerMinutes || !state.sessionStartedAt) return null;
  const limit = state.sessionTimerMinutes * 60;
  return limit - elapsedSeconds(state.sessionStartedAt, now, offsetMs);
}

export function userRemainingSeconds(
  state: ConclaveSpeakingState | null,
  userId: string | undefined,
  now: number,
  offsetMs: number
): number | null {
  if (!state?.userTimerMinutes || !userId) return null;
  const row = state.speakers.find((speaker) => speaker.userId === userId);
  const closed = Math.floor((row?.elapsedMs ?? 0) / 1000);
  const live = row?.activeStartedAt ? elapsedSeconds(row.activeStartedAt, now, offsetMs) : 0;
  return state.userTimerMinutes * 60 - closed - live;
}
